import {
  Fragment,
  useEffect,
  useRef,
  useState,
  useCallback,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { OverflowTip } from './Hover/OverflowTip'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'

export type ActionMenuItem = {
  key: string
  label: ReactNode
  onSelect: () => void
  icon?: ReactNode
  /** Keyboard hint shown on the right (e.g. "⌘D", "↵"). */
  meta?: ReactNode
  tone?: 'default' | 'warn'
  disabled?: boolean
  isActive?: boolean
  title?: string
  dividerBefore?: boolean
  groupHeading?: string
}

export type ActionMenuProps = {
  items: ActionMenuItem[]
  ariaLabel: string
  /**
   * Trigger style.
   * - `kebab`: 30×30 button with ⋮ icon.
   * - `pill`: breadcrumb-pill `› Label` button.
   * - `labeledPill`: `LABEL | value ⌄` button (default).
   */
  trigger?: 'kebab' | 'pill' | 'labeledPill'
  /** Label for `pill` and `labeledPill` triggers. */
  label?: ReactNode
  /** Value text shown on the right of the `labeledPill` trigger. */
  value?: ReactNode
  size?: 'sm' | 'md'
  /**
   * Escape hatch: pass a render prop to supply a fully custom trigger element.
   * ActionMenu will call it with `{ open, toggle, anchorRef }` and use the
   * resulting element as-is.  Kept only for DropdownMenu's wrapper; Phase 4
   * removes it.
   */
  /** Disables the trigger button so the menu cannot be opened. */
  disabled?: boolean
  renderTrigger?: (state: {
    open: boolean
    toggle: () => void
    anchorRef: (node: HTMLButtonElement | null) => void
    disabled: boolean
  }) => ReactNode
  placement?: FloatingMenuPlacement
  matchAnchorWidth?: boolean
  minWidth?: number
  /** Force search on. Auto-enabled when items.length >= 8. */
  searchable?: boolean
  emptyLabel?: string
  className?: string
  style?: CSSProperties
}

/** Returns indices of enabled items in the visible (possibly filtered) list. */
function enabledIndices(items: ActionMenuItem[]): number[] {
  return items
    .map((item, idx) => (item.disabled ? -1 : idx))
    .filter((idx) => idx !== -1)
}

/**
 * ActionMenu — opens a list of actions (not a value picker).
 * Trigger styles: `kebab` (⋮ icon button), `pill` (breadcrumb style),
 * `labeledPill` (LABEL | value ⌄).
 * Absorbs `DropdownMenu`, `PillMenu`, and `LabeledDropdown`'s action use.
 */
export const ActionMenu = ({
  items,
  ariaLabel,
  trigger = 'kebab',
  label,
  value,
  size = 'md',
  disabled = false,
  renderTrigger,
  placement = 'bottom-start',
  matchAnchorWidth = false,
  minWidth,
  searchable,
  emptyLabel = getNlsString('NLSCommonEmptyNoActions'),
  className,
  style,
}: ActionMenuProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  /** Index into `visible` of the currently focused menu item. */
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])

  const setAnchor = useCallback((node: HTMLButtonElement | null) => {
    anchorRef.current = node
  }, [])

  const toggle = () => {
    if (disabled) return
    setOpen((prev) => !prev)
  }
  const close = () => {
    setOpen(false)
    anchorRef.current?.focus()
  }

  const useSearch = searchable ?? items.length >= 8

  const visible: ActionMenuItem[] = useSearch && query
    ? items.filter((item) => {
        const text = typeof item.label === 'string' ? item.label : ''
        return text.toLowerCase().includes(query.trim().toLowerCase())
      })
    : items

  // Reset search and initialize focus on open/close transition during render.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      setQuery('')
      setFocusedIdx(null)
    } else {
      const enabled = enabledIndices(visible)
      setFocusedIdx(enabled[0] ?? null)
    }
  }

  // Focus management — move focus when focusedIdx changes.
  useEffect(() => {
    if (!open || focusedIdx === null) return
    itemRefs.current[focusedIdx]?.focus()
  }, [open, focusedIdx])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const enabled = enabledIndices(visible)
    if (enabled.length === 0) return

    const currentPos = focusedIdx === null ? -1 : enabled.indexOf(focusedIdx)

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const next = currentPos < enabled.length - 1 ? currentPos + 1 : 0
        setFocusedIdx(enabled[next])
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prev = currentPos > 0 ? currentPos - 1 : enabled.length - 1
        setFocusedIdx(enabled[prev])
        break
      }
      case 'Home': {
        event.preventDefault()
        setFocusedIdx(enabled[0])
        break
      }
      case 'End': {
        event.preventDefault()
        setFocusedIdx(enabled[enabled.length - 1])
        break
      }
      default:
        break
    }
  }

  // Build trigger element.
  let triggerElement: ReactNode

  if (renderTrigger) {
    const TriggerComp = renderTrigger
    triggerElement = <TriggerComp open={open} toggle={toggle} anchorRef={setAnchor} disabled={disabled} />
  } else if (trigger === 'kebab') {
    triggerElement = (
      <IconButton
        ref={anchorRef}
        variant="tertiary"
        size="sm"
        icon={<MoreVertRoundedIcon fontSize="small" />}
        label={ariaLabel}
        className={`kebab${open ? ' isOpen' : ''}${disabled ? ' isDisabled' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={toggle}
      />
    )
  } else if (trigger === 'pill') {
    triggerElement = (
      <span className="breadcrumb breadcrumbPill breadcrumbCrumb" data-breadcrumb-menu-root="true">
        <Pressable
          ref={anchorRef}
          className={`breadcrumbChevron${open ? ' isOpen' : ''}`}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          onClick={toggle}
        >
          <span className="breadcrumbChevronIcon" aria-hidden="true">›</span>
        </Pressable>
        {typeof label === 'string' || typeof label === 'number' ? (
          <OverflowTip label={String(label)}>
            <Pressable
              className="breadcrumbLabel"
              aria-disabled={disabled || undefined}
              disabled={disabled}
              onClick={toggle}
            >
              {label}
            </Pressable>
          </OverflowTip>
        ) : (
          <Pressable
            className="breadcrumbLabel"
            aria-disabled={disabled || undefined}
            disabled={disabled}
            onClick={toggle}
          >
            {label}
          </Pressable>
        )}
      </span>
    )
  } else {
    // labeledPill
    const pillClass = [
      'labeledDropdownTrigger',
      size === 'sm' ? 'labeledDropdownTrigger--sm' : '',
      open ? 'isOpen' : '',
      disabled ? 'isDisabled' : '',
    ]
      .filter(Boolean)
      .join(' ')

    triggerElement = (
      <Pressable
        ref={anchorRef}
        className={pillClass}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={toggle}
      >
        <span className="labeledDropdownLabel">{label}</span>
        <span className="labeledDropdownDivider" aria-hidden="true" />
        {typeof value === 'string' || typeof value === 'number' ? (
          <OverflowTip label={String(value)}>
            <span className="labeledDropdownValue">{value}</span>
          </OverflowTip>
        ) : (
          <span className="labeledDropdownValue">{value}</span>
        )}
        <span className="labeledDropdownCaret" aria-hidden="true">
          <ExpandMoreRoundedIcon fontSize="inherit" />
        </span>
      </Pressable>
    )
  }

  return (
    <span
      className={`unifiedActionMenu ${className ?? ''}`.trim()}
      style={style}
      data-floating-menu-root="true"
    >
      {triggerElement}
      <FloatingMenu
        anchorRef={anchorRef as RefObject<HTMLElement | null>}
        isOpen={open}
        onClose={close}
        placement={placement}
        matchAnchorWidth={matchAnchorWidth}
        minWidth={minWidth}
        className="dsDrop is-open"
        role="menu"
        ariaLabel={ariaLabel}
      >
        {useSearch && (
          <div className="dsDrop__search">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={getNlsString('NLSCommonInputSearchPlaceholder')}
            />
          </div>
        )}
        <div className="dsDrop__items" onKeyDown={handleKeyDown}>
          {visible.length === 0 ? (
            <span className="dsDrop__empty">{emptyLabel}</span>
          ) : (
            visible.map((item, idx) => (
              <Fragment key={item.key}>
                {item.dividerBefore ? <div className="dsDrop__divider" role="separator" /> : null}
                {item.groupHeading ? (
                  <div className="dsDrop__groupHeader" role="presentation">
                    {item.groupHeading}
                  </div>
                ) : null}
                <Pressable
                  ref={(node) => { itemRefs.current[idx] = node }}
                  role="menuitem"
                  tabIndex={focusedIdx === idx ? 0 : -1}
                  disabled={item.disabled}
                  title={item.title}
                  data-tone={item.tone === 'warn' ? 'warn' : undefined}
                  className={`dsDrop__item${item.isActive ? ' is-active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (item.disabled) return
                    item.onSelect()
                    setOpen(false)
                    anchorRef.current?.focus()
                  }}
                >
                  {item.icon && (
                    <span className="dsDrop__itemIcon" aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  <span className="dsDrop__itemLabel">{item.label}</span>
                  {item.meta && (
                    <span className="dsDrop__itemMeta">{item.meta}</span>
                  )}
                </Pressable>
              </Fragment>
            ))
          )}
        </div>
      </FloatingMenu>
    </span>
  )
}
